import {
  useRef,
  type MutableRefObject,
} from "react";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";
import type { SessionCollab } from "./context";
import {
  useSessionProviderConversationState,
  useSessionProviderEntityState,
  useSessionProviderInteractionState,
  useSessionProviderMetaState,
} from "./use-session-provider-state";
import { useLatestSessionValueRef } from "./use-session-synced-refs";

export type SessionProviderMetaState =
  ReturnType<typeof useSessionProviderMetaState>;
export type SessionProviderEntityState =
  ReturnType<typeof useSessionProviderEntityState>;
export type SessionProviderConversationState =
  ReturnType<typeof useSessionProviderConversationState>;
export type SessionProviderInteractionState =
  ReturnType<typeof useSessionProviderInteractionState>;

export interface SessionProviderStateRefs {
  sessionGenerationRef: MutableRefObject<number>;
  membersRef: MutableRefObject<Record<string, Member>>;
  productsRef: MutableRefObject<Record<string, ProductSummary>>;
  collabsByProductRef: MutableRefObject<Record<string, string[]>>;
  chatMessageIdsRef: MutableRefObject<string[]>;
  rootProductIdRef: MutableRefObject<string | null>;
}

export interface SessionProviderStateBundle {
  meta: SessionProviderMetaState;
  entity: SessionProviderEntityState;
  conversation: SessionProviderConversationState;
  interaction: SessionProviderInteractionState;
  refs: SessionProviderStateRefs;
}

export function useSessionProviderStateBundle(): SessionProviderStateBundle {
  const meta = useSessionProviderMetaState();
  const entity = useSessionProviderEntityState();
  const conversation = useSessionProviderConversationState();
  const interaction = useSessionProviderInteractionState();
  const sessionGenerationRef = useRef<number>(0);
  const membersRef = useLatestSessionValueRef(entity.members);
  const productsRef = useLatestSessionValueRef(entity.products);
  const collabsByProductRef = useLatestSessionValueRef(
    conversation.collabsByProduct,
  );
  const chatMessageIdsRef = useLatestSessionValueRef(
    conversation.chatMessageIds,
  );
  const rootProductIdRef = useRef<string | null>(null);

  return {
    meta,
    entity,
    conversation,
    interaction,
    refs: {
      sessionGenerationRef,
      membersRef,
      productsRef,
      collabsByProductRef,
      chatMessageIdsRef,
      rootProductIdRef,
    },
  };
}
